/** A title, a category pill row that filters the grid under it, and that grid. */
import { useSelection } from './ui';
import { Surface, Vertical } from './primitives';
import { ItemCollection } from './collections';

const CHIP = {
  minHeight: 32.66,
  borderRadius: 24,
  borderWidth: 0.56,
  padding: '0 16.55px',
  fontSize: 13,
  fontWeight: 600,
  // A category name is blueprint-supplied: keep every chip on one line and let
  // the row scroll. A minimum height also leaves room for larger text.
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  flex: '0 0 auto',
  cursor: 'pointer',
  background: 'none',
};

const optionsOf = (props) => (Array.isArray(props.options) ? props.options : []);

// Keep the local choice and the two deliveries it consumed in the host store.
// Until a user tap consumes them, applyGroup keeps its documented precedence
// over setMode, matching Flutter. A new delivery can still select afterwards.
// eslint-disable-next-line react-refresh/only-export-components -- shared shelf selection hook
export function useShelfSelection(nodeId, props, slots, options) {
  const [choice, setChoice] = useSelection(nodeId, { key: null, group: null, mode: null });
  const group = slots?.applyGroup;
  const mode = slots?.setMode;
  const active = (group !== choice.group ? group?.payload?.key : null)
    ?? (mode !== choice.mode ? mode?.payload?.key : null)
    ?? choice.key ?? props.select ?? options[0]?.key ?? null;
  return [active, (key) => setChoice({ key, group, mode })];
}

export function CategoryShelf({ nodeId, node, props = {}, data, ctx, emit, slots, renderTemplate }) {
  const options = optionsOf(props);
  const [active, setActive] = useShelfSelection(nodeId, props, slots, options);

  const pick = (key) => {
    setActive(key);
    emit?.('modeChanged', { key });
  };

  return (
    <Surface variant="card" props={{ padding: '4', radius: 28 }}>
      <Vertical props={{ gap: '3' }}>
        {props.title ? (
          <div style={{
            fontSize: 'var(--t-xl)',
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            letterSpacing: -0.3,
            color: 'var(--text)',
          }}>
            {props.title}
          </div>
        ) : null}
        {options.length ? (
          <div style={{ display: 'flex', gap: 7.99, overflowX: 'auto', paddingBottom: 8 }}>
            {options.map((o) => {
              const on = String(o.key) === String(active);
              return (
                <button
                  key={o.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => pick(o.key)}
                  style={{
                    ...CHIP,
                    background: on ? 'var(--text)' : 'var(--surface)',
                    color: on ? 'var(--surface)' : 'var(--text)',
                    border: `${CHIP.borderWidth}px solid ${on ? 'var(--text)' : 'var(--line)'}`,
                  }}
                >
                  {o.label ?? o.key}
                </button>
              );
            })}
          </div>
        ) : null}
        <ItemCollection
          nodeId={nodeId}
          node={node}
          props={{
            layout: 'grid',
            pick: active,
            columns: props.columns ?? 3,
            aspect: props.aspect ?? '1/1',
            gap: '3',
            items: props.items,
            card: props.card,
            pending: props.pending,
            crud: props.crud,
            pendingScope: props.pendingScope,
            filterField: props.filterField,
            emptyTitle: props.emptyTitle,
            emptyBody: props.emptyBody,
          }}
          data={data}
          ctx={ctx}
          emit={emit}
          slots={{ ...slots, applyGroup: undefined }}
          renderTemplate={renderTemplate || props.renderTemplate}
        />
      </Vertical>
    </Surface>
  );
}

export default CategoryShelf;
